import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mic, Upload, ArrowRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, Empty, PageHeader, Stat, StatusBadge } from "@/components/app/StatusBadge";
import { api, fmtTime } from "@/lib/api";
import { FreeLimitBanner } from "@/components/billing/FreeLimitBanner";
import { useApp } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

const UsageBar = ({ label, used, quota, id }) => (
  <div data-testid={`usage-${id}`}>
    <div className="flex justify-between text-xs font-semibold text-slate-600"><span>{label}</span><span>{used} / {quota}</span></div>
    <Progress value={quota ? Math.min(100, (used / quota) * 100) : 0} className="mt-2 h-2" />
  </div>
);

export default function Dashboard() {
  const { L } = useApp();
  const { user, workspace } = useAuth();
  const [d, setD] = useState(null);

  useEffect(() => {
    if (!workspace) return;
    const load = () => api.get("/dashboard", { params: { workspace_id: workspace.id } }).then((r) => setD(r.data));
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [workspace]);

  const processing = d ? Object.entries(d.status_counts).filter(([s]) => !["completed", "failed"].includes(s)).reduce((n, [, v]) => n + v, 0) : 0;
  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-8" data-testid="dashboard-page">
      <PageHeader title={`${L("Selamat datang", "Welcome")}, ${user.name}`} desc={workspace?.name}>
        <Button asChild variant="outline" className="rounded-xl" data-testid="dash-upload-btn"><Link to="/app/upload"><Upload size={16} className="mr-1" /> {L("Muat naik", "Upload")}</Link></Button>
        <Button asChild className="rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="dash-record-btn"><Link to="/app/record"><Mic size={16} className="mr-1" /> {L("Rakam", "Record")}</Link></Button>
      </PageHeader>
      <FreeLimitBanner />
      {!d ? <Skeleton className="mt-8 h-40 rounded-2xl" /> : (
        <>
          <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-3">
            <Stat id="total" label={L("Jumlah mesyuarat", "Total meetings")} value={d.total_meetings} />
            <Stat id="processing" label={L("Sedang diproses", "Processing")} value={processing} sub={`${d.status_counts.failed || 0} ${L("gagal", "failed")}`} />
            {d.usage.plan.key === "free"
              ? <Stat id="minutes-left" label={L("Transkripsi percuma", "Free transcription")} value={`${d.usage.free_used ?? 0}/${d.usage.plan.lifetime_transcriptions}`} sub={`${L("Pelan", "Plan")} ${d.usage.plan.name}`} />
              : <Stat id="minutes-left" label={L("Baki minit", "Minutes left")} value={d.usage.minutes_left} sub={`${L("Pelan", "Plan")} ${d.usage.plan.name}`} />}
          </div>
          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            <Card className="p-6 lg:col-span-2">
              <div className="flex items-center justify-between"><h2 className="font-semibold">{L("Mesyuarat terkini", "Recent meetings")}</h2><Link to="/app/meetings" className="text-sm font-semibold text-violet-700" data-testid="dash-all-meetings">{L("Lihat semua", "View all")}</Link></div>
              <div className="mt-4 divide-y divide-slate-100">
                {d.recent.length === 0 && <Empty>{L("Belum ada mesyuarat. Mulakan dengan rakam atau muat naik.", "No meetings yet. Start by recording or uploading.")}</Empty>}
                {d.recent.map((m) => (
                  <Link key={m.id} to={`/app/meetings/${m.id}`} className="flex items-center justify-between gap-3 py-3 hover:bg-slate-50" data-testid={`dash-meeting-${m.id}`}>
                    <div className="min-w-0"><p className="truncate font-medium">{m.title}</p><p className="flex items-center gap-2 text-xs text-slate-500">{m.meeting_date} · <Clock size={11} /> {fmtTime(m.duration)}</p></div>
                    <StatusBadge status={m.status} />
                  </Link>
                ))}
              </div>
            </Card>
            <Card className="p-6">
              <h2 className="font-semibold">{d.usage.plan.key === "free" ? L("Penggunaan", "Usage") : L("Penggunaan kitaran ini", "Usage this cycle")}</h2>
              <div className="mt-5 space-y-5">
                {d.usage.plan.key === "free"
                  ? <UsageBar id="minutes" label={L("Transkripsi percuma (seumur hidup)", "Free transcription (lifetime)")} used={d.usage.free_used ?? 0} quota={d.usage.plan.lifetime_transcriptions} />
                  : <UsageBar id="minutes" label={L("Minit transkripsi", "Transcription minutes")} used={d.usage.minutes_used} quota={d.usage.minutes_quota} />}
                {d.usage.plan.key === "free"
                  ? <p className="text-sm text-slate-500" data-testid="dash-docs-pro-only">{L("Jana dokumen: mulai pelan Lite", "Document generation: from the Lite plan")}</p>
                  : <UsageBar id="credits" label={L("Jana dokumen", "Document generations")} used={d.usage.credits_used} quota={d.usage.credits_quota} />}
              </div>
              {d.usage.plan.key !== "free" && (d.usage.minutes_left < 60 || d.usage.credits_left < 3) && (
                <Link to="/app/billing#topup" className="mt-5 block rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm font-semibold text-amber-900" data-testid="dash-topup-cta">
                  {L("Kuota hampir habis — Tambah kuota", "Quota almost used — Add quota")} <ArrowRight size={14} className="inline" />
                </Link>
              )}
              <Link to="/app/billing" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-violet-700">{L("Urus langganan", "Manage plan")} <ArrowRight size={14} /></Link>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
